import { Menu, MenuItem, Tooltip, Button } from "@mui/material";
import styled from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import DropdownIcon from "@mui/icons-material/ArrowDropDown";
import { usePageInUrl } from "./MachineSwitcherDropdown";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import {
  useAnimationsEnabled,
  useGetPageFromPath,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { BREAKPOINT_DESKTOP_XL, useBreakpoints } from "constants/constants";
import { useDispatch } from "react-redux";
import {
  clearAllAggregates,
  clearAllGroupsAndProcesses,
  clearAllSegments,
  clearAllTrendChartBars,
} from "providers/GlobalStateProvider";
import { snakeCase } from "lodash";
import { animated, useSpring } from "@react-spring/web";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { useShowProcessGroupSwitcherDropdown } from "./topBarUtils";
import { useBreadcrumbDropdowns } from "./useBreadcrumbDropdowns";

const WorkScheduleSwitcherDropdownStyles = styled.h6`
  display: flex;
  justify-content: start;
  flex-grow: 1;
  @media screen and (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    .dropdownArrow {
      font-size: 36px;
    }
  }
`;

export function WorkScheduleSwitcherDropdown() {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const { translate } = useFlightTranslation();
  const [workSchedules] = useWorkScheduleConfigJsonListAtom();
  const navigate = useNavigate();
  const pageInUrl = usePageInUrl();
  const dispatch = useDispatch();
  const { isTier0Page, isAllWorkSchedulesPage, isGroupTilesPage } =
    useGetPageFromPath();

  // Extract the current path from the URL to preserve path type (reports, dashboard, etc.)
  const { pathname } = useLocation();

  const handleNavigateToWsId = useCallback(
    wsId => {
      dispatch(clearAllTrendChartBars());
      dispatch(clearAllSegments());
      dispatch(clearAllAggregates());
      dispatch(clearAllGroupsAndProcesses(true));
      const firstProcessInWorkSchedule = workSchedules.find(
        ws => ws.id === wsId
      )?.processes?.[0]?.taps?.[0]?.name;

      // Check if we're on a reports page and preserve that path when switching work schedules
      const isReportsPage = pathname.includes("/reports");
      const basePath = isReportsPage
        ? pathname.split("/reports")[0] + "/reports" // go back to reports page
        : pageInUrl; // Use the existing pageInUrl for other pages

      const nextUrl = `${basePath}?workSchedule=${wsId}${
        isTier0Page
          ? // on tier0 page, add "process={first process in the workschedule}"
            `&process=${firstProcessInWorkSchedule}`
          : ""
      }`;
      navigate(nextUrl);
      handleClose();
    },
    [dispatch, navigate, pageInUrl, isTier0Page, workSchedules, pathname]
  );

  // if showWSDropdown is false, animate the dropdown out to the left if it's false
  const { showWSDropdown } = useBreadcrumbDropdowns();

  const showProcessGroupSwitcherDropdown =
    useShowProcessGroupSwitcherDropdown();
  const { isTabletOrLarger } = useBreakpoints();
  // if we're on mobile width AND pg dropdown should be visible (on desktop width) THEN  animates out to the left
  const shouldAnimateOutLeft =
    !isTabletOrLarger && showProcessGroupSwitcherDropdown;

  const animationsEnabled = useAnimationsEnabled();

  const springLeftRight = useSpring({
    ...(animationsEnabled
      ? {
          transform: `translateX(${
            showWSDropdown
              ? 0
              : 0.5 * innerWidth - 58 - 96 * (shouldAnimateOutLeft ? -1 : 1)
          }px)`,
        }
      : {}),
    opacity: showWSDropdown ? 1 : 0,
    config: showWSDropdown
      ? {
          /* default config */ tension: 170,
          friction: 26,
          mass: 1,
        }
      : {
          /* a bit slower on the way out */ tension: 100,
          friction: 30,
          mass: 1,
        },
  });

  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const currentWorkSchedule = workSchedules.find(
    ws => ws.id === workScheduleId
  );

  const showWsSwitcherDropdownArrow = !isAllWorkSchedulesPage;
  let workScheduleNameDisplay = currentWorkSchedule?.name;
  if (isAllWorkSchedulesPage && !isGroupTilesPage) {
    workScheduleNameDisplay = "All";
  }

  return (
    <animated.div
      style={{ ...springLeftRight, width: showWSDropdown ? "auto" : "0px" }}
      className="workscheduleswitcher"
    >
      <WorkScheduleSwitcherDropdownStyles
        className="name"
        data-component-name={"Schedule Switcher Dropdown"}
      >
        <Tooltip
          title={
            showWsSwitcherDropdownArrow ? translate(`Switch_schedules`) : ""
          }
        >
          <Button
            aria-controls="schedule-switcher-menu"
            aria-haspopup="true"
            className="btnDropDown"
            onClick={showWsSwitcherDropdownArrow ? handleClick : () => null}
            style={
              !showWsSwitcherDropdownArrow ? { pointerEvents: "none" } : {}
            }
            endIcon={
              showWsSwitcherDropdownArrow ? (
                <DropdownIcon className="dropdownArrow" />
              ) : null
            }
            disabled={!showWsSwitcherDropdownArrow}
          >
            <div className="departmentName">{workScheduleNameDisplay}</div>
          </Button>
        </Tooltip>
        <Menu
          id="schedule-switcher-menu"
          anchorEl={anchorEl}
          keepMounted={false}
          disableScrollLock={true}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
        >
          {workSchedules.map((ws, wsIdx) => {
            return (
              <MenuItem
                className={`group-switcher-menu-item ${snakeCase(ws.name)}`}
                key={wsIdx}
                disabled={ws.name === currentWorkSchedule?.name}
                onClick={() => handleNavigateToWsId(ws.id)}
              >
                {ws.name}
              </MenuItem>
            );
          })}
        </Menu>
      </WorkScheduleSwitcherDropdownStyles>
    </animated.div>
  );
}
